export interface JwtClaims {
  sub?: string;
  email?: string;
  name?: string;
  [claim: string]: unknown;
}

/** Zitadel project role that grants access to admin features. */
const ADMIN_ROLE = 'ADMINISTRATOR';

/** Zitadel project role that lets a user choose a style guide per file (COPA-913). */
const MANAGER_ROLE = 'MANAGER';

export function decodeJwt(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replaceAll('-', '+').replaceAll('_', '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.codePointAt(0)!.toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * Whether the token grants a Zitadel project role. Zitadel emits project roles as an object under
 * `urn:zitadel:iam:org:project:roles` (and the project-scoped `urn:zitadel:iam:org:project:{id}:roles`); each granted
 * role name is a key in that object.
 */
export function hasProjectRole(token: string | null, role: string): boolean {
  if (!token) return false;
  const claims = decodeJwt(token);
  if (!claims) return false;
  return Object.entries(claims).some(
    ([key, value]) =>
      key.startsWith('urn:zitadel:iam:org:project') &&
      key.endsWith(':roles') &&
      typeof value === 'object' &&
      value !== null &&
      Object.hasOwn(value, role),
  );
}

/** Admin access is granted via the Zitadel project role "ADMINISTRATOR". */
export function isAdminToken(token: string | null): boolean {
  return hasProjectRole(token, ADMIN_ROLE);
}

/** A manager or an administrator may choose a style guide for each file; a requester always gets the default. */
export function canChooseStyleGuideToken(token: string | null): boolean {
  return hasProjectRole(token, MANAGER_ROLE) || hasProjectRole(token, ADMIN_ROLE);
}

/** A manager or an administrator may choose the XTM Cloud customer and template for each file (COPA-926). */
export function canChooseXtmCloudTargetToken(token: string | null): boolean {
  return hasProjectRole(token, MANAGER_ROLE) || hasProjectRole(token, ADMIN_ROLE);
}
